import type { AuthUser } from '@plane/shared';
import type { LoginInput } from '@plane/shared';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { AuthContext, type AuthState } from '@/modules/auth/hooks/auth-context';
import {
  login as loginRequest,
  logout as logoutRequest,
  fetchCurrentUser,
} from '@/modules/auth/services/auth.service';
import { refreshAccessToken, setSessionEndedListener } from '@/services/http-client';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<AuthUser | null>(null);
  const [carregando, setCarregando] = useState(true);

  // O cliente HTTP avisa quando uma renovação falha de vez. Sem isto, o painel
  // ficaria exibindo uma tela cheia de dados de uma sessão que já não existe.
  useEffect(() => {
    setSessionEndedListener(() => {
      setUsuario(null);
    });

    return () => {
      setSessionEndedListener(null);
    };
  }, []);

  // Reidratação: o access token vive em memória e morre no F5, mas o cookie
  // httpOnly sobrevive. Uma renovação na abertura recupera a sessão.
  useEffect(() => {
    let ativo = true;

    void (async () => {
      try {
        const token = await refreshAccessToken();

        if (!token) return;

        const atual = await fetchCurrentUser();

        if (ativo) setUsuario(atual);
      } catch {
        // Sem sessão recuperável. Não é erro a relatar: é o estado normal de
        // quem ainda não entrou.
      } finally {
        if (ativo) setCarregando(false);
      }
    })();

    return () => {
      ativo = false;
    };
  }, []);

  const entrar = useCallback(async (input: LoginInput) => {
    setUsuario(await loginRequest(input));
  }, []);

  const sair = useCallback(async () => {
    await logoutRequest();
    setUsuario(null);
  }, []);

  const valor = useMemo<AuthState>(
    () => ({ usuario, carregando, entrar, sair }),
    [usuario, carregando, entrar, sair],
  );

  return <AuthContext value={valor}>{children}</AuthContext>;
}
